import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import DataTable, { type Column } from '@/components/base/DataTable';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import { PriorityBadge, StatusBadge } from '@/components/feature/StatusBadge';
import { formatCurrency } from '@/lib/format';
import type { OrderView } from '@/types';

interface NewOrdersSectionProps {
  orders: OrderView[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  currency: string;
}

export default function NewOrdersSection({
  orders,
  loading,
  error,
  onRetry,
  currency,
}: NewOrdersSectionProps) {
  const { t } = useTranslation();

  const columns: Column<OrderView>[] = [
    {
      key: 'id',
      header: t('home.newOrders.col.order'),
      render: (o) => (
        <span className="lb-ltr-field whitespace-nowrap font-semibold text-foreground-950">{o.id}</span>
      ),
    },
    {
      key: 'customer',
      header: t('home.newOrders.col.customer'),
      render: (o) => <span className="font-medium text-foreground-900">{o.customerName}</span>,
    },
    {
      key: 'priority',
      header: t('home.newOrders.col.priority'),
      render: (o) => <PriorityBadge priority={o.priority} />,
    },
    {
      key: 'fulfillment',
      header: t('home.newOrders.col.fulfillment'),
      render: (o) => <span className="text-foreground-700">{t(`fulfillment.${o.fulfillment}`)}</span>,
    },
    {
      key: 'total',
      header: t('home.newOrders.col.total'),
      render: (o) => <span className="font-semibold text-foreground-900">{formatCurrency(o.total, currency)}</span>,
    },
    {
      key: 'status',
      header: t('home.newOrders.col.status'),
      render: (o) => <StatusBadge status={o.status} />,
    },
    {
      key: 'action',
      header: t('home.newOrders.col.action'),
      className: 'text-end',
      render: (o) => (
        <Link
          to={`/dashboard/orders/${o.id}`}
          className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
        >
          <i className="ri-eye-line text-base" aria-hidden="true" />
          {t('common.viewDetails')}
        </Link>
      ),
    },
  ];

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
        <div>
          <h2 className="text-base font-semibold text-foreground-950">{t('home.newOrders.title')}</h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('home.newOrders.subtitle')}</p>
        </div>
        <Link
          to="/dashboard/orders?tab=new"
          className="inline-flex h-10 items-center gap-1.5 rounded-md bg-secondary-100 border border-secondary-200 px-4 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
        >
          <i className="ri-arrow-left-line text-lg" aria-hidden="true" />
          {t('home.newOrders.viewAll')}
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3 p-4 md:p-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 flex-1" />
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-4 md:p-5">
          <ErrorState
            compact
            title={t('home.ordersError')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={onRetry}
          />
        </div>
      ) : !orders || orders.length === 0 ? (
        <div className="p-4 md:p-5">
          <EmptyState icon="ri-inbox-archive-line" title={t('home.newOrders.empty')} />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <DataTable columns={columns} rows={orders} rowKey={(o) => o.id} />
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-background-100 md:hidden">
            {orders.map((o) => (
              <li key={o.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="lb-ltr-field text-sm font-semibold text-foreground-950">{o.id}</p>
                    <p className="mt-0.5 truncate text-sm font-medium text-foreground-800">{o.customerName}</p>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <PriorityBadge priority={o.priority} />
                  <span className="text-xs text-foreground-500">{t(`fulfillment.${o.fulfillment}`)}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-foreground-900">
                    {formatCurrency(o.total, currency)}
                  </span>
                  <Link
                    to={`/dashboard/orders/${o.id}`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-background-300 px-3 text-xs font-medium text-foreground-700 hover:bg-background-100"
                  >
                    <i className="ri-eye-line text-base" aria-hidden="true" />
                    {t('common.viewDetails')}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}